/* ============================================================================
   UI / overlay — things drawn over or beside the page.
   Tooltip, Modal (dialog or sheet), ConfirmDialog / useConfirm (a question before something is lost), PromptDialog / usePrompt (a question with a typed answer), Disclosure (folded group), SidePanel (detail beside a list).
   ============================================================================ */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "./actions.jsx";
import { Field, Input, Textarea } from "./form.jsx";
import { cx } from "./util.js";

/* A short note that shows on hover or keyboard focus, over everything, kept inside the window (it flips below its
   anchor when there is no room above). `content` is what it says; `block` makes the anchor an inline block; `tone="raised"`
   draws it as a light card instead of the dark bubble, for a note that holds a small list. */
export function Tooltip({ content, width = 300, block = false, tone, children }) {
  const anchor = useRef(null);
  const tip = useRef(null);
  const [at, setAt] = useState(null);
  const [pos, setPos] = useState(null);
  const show = () => {
    const r = anchor.current?.getBoundingClientRect();
    if (r) setAt({ cx: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  };
  const hide = () => { setAt(null); setPos(null); };
  useLayoutEffect(() => {
    const r = tip.current?.getBoundingClientRect();
    if (!at || !r) return;
    const pad = 10;
    const above = at.top - r.height - 9;
    const top = above >= pad ? above : Math.min(at.bottom + 9, window.innerHeight - r.height - pad);
    const left = Math.max(pad, Math.min(at.cx - r.width / 2, window.innerWidth - r.width - pad));
    setPos({ top, left });
  }, [at]);
  if (!content) return children;
  return (
    <span ref={anchor} className={cx("ds-tooltip-anchor", block && "is-block")} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      {children}
      {at && createPortal(
        <div ref={tip} role="tooltip" className={cx("ds-tooltip", tone && `ds-tooltip--${tone}`, !pos && "is-measuring")}
          style={{ "--tip-left": `${pos ? pos.left : at.cx}px`, "--tip-top": `${pos ? pos.top : at.top}px`, "--tip-w": `${Math.min(width, window.innerWidth - 20)}px` }}>
          {content}
        </div>,
        document.body,
      )}
    </span>
  );
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/* A dialog over a dimmed page, or with `side` a sheet that slides in from the right. Sizes are sm / md / lg / xl.
   Escape closes it unless `closable` is false (work in progress); a click on the dimmed page closes it only when
   `closeOnBackdrop` is set, so a half-filled form is never lost to a stray click. `open={false}` keeps it mounted
   but hidden, for a surface whose state must survive closing. `leading` is a mark before the title (a person's avatar). */
export function Modal({ title, subtitle, leading, actions, children, onClose, size = "md", side = false, open = true, closable = true, closeOnBackdrop = false }) {
  const dialogRef = useRef(null);
  // Taken while rendering, before any field inside with autoFocus can take the focus; otherwise the "opener"
  // would be that field, which is gone when the dialog closes, and focus would fall back to the page.
  const openerRef = useRef(typeof document === "undefined" ? null : document.activeElement);
  // A dialog that mounted closed and opens later takes its opener at that moment instead.
  const startedOpen = useRef(open);
  const firstOpen = useRef(true);
  useEffect(() => {
    if (!open || !closable) return undefined;
    // Only the dialog on top answers Escape: a question opened over a sheet must not close the sheet with it.
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      const shown = document.querySelectorAll(".Modal-Backdrop:not([hidden])");
      if (shown[shown.length - 1] === dialogRef.current?.parentElement) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, closable, open]);
  // Move focus into the dialog when it opens (unless a field inside already took it)
  // and hand it back to whatever opened the dialog when it closes.
  useEffect(() => {
    if (!open) return undefined;
    if (!(startedOpen.current && firstOpen.current)) openerRef.current = document.activeElement;
    firstOpen.current = false;
    const opener = openerRef.current;
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();
    return () => { if (opener && opener.isConnected) opener.focus?.(); };
  }, [open]);
  // Keep Tab / Shift+Tab inside the dialog.
  const keepFocusInside = (event) => {
    if (event.key !== "Tab") return;
    const items = [...dialogRef.current.querySelectorAll(FOCUSABLE)];
    if (!items.length) { event.preventDefault(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  const onBackdrop = closeOnBackdrop && closable ? (event) => { if (event.target === event.currentTarget) onClose?.(); } : undefined;
  // Drawn on the document body, not where it was written: no rule of the screen that opened it can reach the overlay
  // (a `.Actions > * { height }` rule once squashed the dossier dialog to a sliver).
  return createPortal(
    <div className={cx("Modal-Backdrop", side && "is-side")} hidden={!open} onMouseDown={onBackdrop}>
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined} tabIndex={-1} onKeyDown={keepFocusInside} className={cx("Modal", `Modal--${size}`, side && "is-side")}>
        <header>
          {leading}
          <div className="Modal-Title">
            <h1>{title}</h1>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {actions}
          {closable && <button type="button" className="Modal-Close-Btn" onClick={onClose} title="Close" aria-label="Close"><X size={18} /></button>}
        </header>
        <div className="Modal-Body">{children}</div>
      </section>
    </div>,
    document.body,
  );
}

/* A question the person must answer before something is archived, deleted or overwritten (it replaces window.confirm).
   `message` says what will happen; `tone="danger"` paints the confirming button red; `busy` locks it while the work runs. */
function ConfirmDialog({ title = "Are you sure?", message, confirmLabel = "Confirm", cancelLabel = "Cancel", tone, busy = false, onConfirm, onCancel }) {
  return (
    <Modal size="sm" title={title} onClose={onCancel}>
      {message && <p className="ds-muted">{message}</p>}
      <div className="Modal-Actions">
        <Button type="button" onClick={onCancel}>{cancelLabel}</Button>
        <Button type="button" variant={tone === "danger" ? "danger" : "primary"} disabled={busy} onClick={onConfirm}>{confirmLabel}</Button>
      </div>
    </Modal>
  );
}

/* The state behind a ConfirmDialog. `ask({ title, message, confirmLabel, tone, onConfirm })` opens the question; it closes
   on either answer, and onConfirm runs only on yes. Render `dialog` anywhere in the screen. */
export function useConfirm() {
  const [question, setQuestion] = useState(null);
  const answer = () => { const { onConfirm } = question; setQuestion(null); onConfirm?.(); };
  const dialog = question ? <ConfirmDialog {...question} onConfirm={answer} onCancel={() => setQuestion(null)} /> : null;
  return { ask: setQuestion, dialog };
}

/* A question that needs a typed answer (it replaces window.prompt): a name, a reason, or the words that confirm something
   that cannot be taken back. `label` names the box; `required` blocks an empty answer; `validate(value)` may return a
   sentence saying what is wrong, which is shown under the box and keeps the dialog open; `multiline` gives a taller box. */
function PromptDialog({ title, message, label, initial = "", confirmLabel = "Continue", cancelLabel = "Cancel", tone, required = false, multiline = false, validate, onSubmit, onCancel }) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState("");
  const submit = (event) => {
    event.preventDefault();
    const text = value.trim();
    const problem = required && !text ? "This is needed to go on." : validate?.(text);
    if (problem) { setError(problem); return; }
    onSubmit(text);
  };
  const Box = multiline ? Textarea : Input;
  return (
    <Modal size="sm" title={title} onClose={onCancel}>
      <form onSubmit={submit} noValidate>
        {message && <p className="ds-muted">{message}</p>}
        <Field label={label} error={error}>
          <Box autoFocus value={value} rows={multiline ? 3 : undefined} aria-invalid={error ? true : undefined}
            onChange={(event) => { setValue(event.target.value); setError(""); }} />
        </Field>
        <div className="Modal-Actions">
          <Button type="button" onClick={onCancel}>{cancelLabel}</Button>
          <Button type="submit" variant={tone === "danger" ? "danger" : "primary"}>{confirmLabel}</Button>
        </div>
      </form>
    </Modal>
  );
}

/* The state behind a PromptDialog. `ask({ title, message, label, initial, confirmLabel, tone, required, multiline, validate,
   onSubmit })` opens it; onSubmit gets the trimmed answer and runs only when it passes. Cancel, Escape and the close
   button end it with no call. Render `dialog` anywhere in the screen. */
export function usePrompt() {
  const [question, setQuestion] = useState(null);
  const submit = (value) => { const { onSubmit } = question; setQuestion(null); onSubmit?.(value); };
  const dialog = question ? <PromptDialog {...question} onSubmit={submit} onCancel={() => setQuestion(null)} /> : null;
  return { ask: setQuestion, dialog };
}

/* A folded group that opens on a click; the summary line says what is inside and how many.
   `meta` is a small status area at the right end of the summary (badges). With `open` and `onToggle` the parent
   owns whether it is open (an "expand all" control); `onToggle` gets the new state.
   `card` frames it as a card; with `icon` (a mark before the title) and `subtitle` (one line under it) the summary
   becomes a heading for a section of a page, and what is inside is spaced as a stack (ERP-1990). */
export function Disclosure({ summary, count, meta, icon, subtitle, defaultOpen = false, open, onToggle, quiet = false, card = false, className, children, ...rest }) {
  const owned = open !== undefined;
  const state = owned
    ? { open, onToggle: (event) => { if (event.currentTarget.open !== open) onToggle?.(event.currentTarget.open); } }
    : { open: defaultOpen };
  return (
    <details className={cx("ds-disclosure", quiet && "ds-disclosure--quiet", card && "ds-disclosure--card", className)} {...state} {...rest}>
      <summary>
        {icon && <span className="ds-disclosure__icon" aria-hidden="true">{icon}</span>}
        {subtitle ? (
          <span className="ds-disclosure__text"><span className="ds-disclosure__title">{summary}</span><span className="ds-disclosure__sub">{subtitle}</span></span>
        ) : <span>{summary}</span>}
        {count != null && <span className="ds-disclosure__count">{count}</span>}
        {meta && <span className="ds-disclosure__meta">{meta}</span>}
      </summary>
      {card ? <div className="ds-disclosure__body">{children}</div> : children}
    </details>
  );
}

function useNarrow(maxWidth) {
  const query = `(max-width: ${maxWidth}px)`;
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const on = () => setNarrow(list.matches);
    list.addEventListener("change", on);
    on();
    return () => list.removeEventListener("change", on);
  }, [query]);
  return narrow;
}

/* The detail of one thing in a list, shown beside the page: the list narrows to make room and the header and
   menu stay usable (a dialog would take the screen). Escape closes it. On a phone there is no room beside, so it
   is a sheet instead. `meta` is a row of badges under the title. */
export function SidePanel({ title, meta, onClose, children }) {
  const narrow = useNarrow(720);
  useEffect(() => {
    if (narrow) return undefined;
    const onKey = (event) => { if (event.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [narrow, onClose]);
  if (narrow) {
    return (
      <Modal side title={title} onClose={onClose} closeOnBackdrop>
        <div className="ds-sidepanel__stack">
          {meta && <div className="ds-toolbar">{meta}</div>}
          {children}
        </div>
      </Modal>
    );
  }
  return (
    <aside className="ds-sidepanel" aria-label={typeof title === "string" ? title : undefined}>
      <header className="ds-sidepanel__head">
        <div>
          <h3>{title}</h3>
          {meta && <div className="ds-toolbar">{meta}</div>}
        </div>
        <button type="button" className="Modal-Close-Btn" onClick={onClose} title="Close" aria-label="Close"><X size={18} /></button>
      </header>
      <div className="ds-sidepanel__body"><div className="ds-sidepanel__stack">{children}</div></div>
    </aside>
  );
}
